/**
 * Inline success / partial / error / notice banners below the add panel.
 * Success messages auto-dismiss from the parent; partial and error need onDismiss.
 * @param {{
 *   lastResult?: object | null,
 *   addedItemNames?: string[],
 *   unrecognizedItems?: Array<{ item_name?: string, reason?: string }>,
 *   onTryManual?: () => void,
 *   onDismiss?: () => void,
 * }} props
 */
import { hasReceiptSkippedItems, receiptSkipReasonLabel } from './pantryAddFeedbackUtils.js'

export default function PantryAddFeedbackBanner({
  lastResult,
  addedItemNames = [],
  unrecognizedItems = [],
  onTryManual,
  onDismiss,
}) {
  if (!lastResult) return null

  const dismissButton = onDismiss ? (
    <button
      type="button"
      className="pantry-add-feedback__dismiss"
      onClick={onDismiss}
      aria-label="Dismiss"
    >
      ×
    </button>
  ) : null

  if (lastResult.notice === 'zero_candidates' || lastResult.notice === 'zero_candidates_pdf') {
    const fromPdf = lastResult.notice === 'zero_candidates_pdf'
    return (
      <div className="pantry-add-feedback pantry-add-feedback--notice" role="status">
        <span className="pantry-add-feedback__icon" aria-hidden="true">
          ℹ
        </span>
        <div className="pantry-add-feedback__body">
          <div className="pantry-add-feedback__title">
            {fromPdf
              ? "We couldn't find any items in that invoice"
              : "We couldn't find any items in that text"}
          </div>
          <div className="pantry-add-feedback__sub">
            {fromPdf
              ? 'Paste the receipt text instead.'
              : 'Double check the paste below, then try again.'}
          </div>
        </div>
        <div className="pantry-add-feedback__actions">
          {onTryManual ? (
            <button type="button" className="pantry-add-feedback__retry" onClick={onTryManual}>
              Try again
            </button>
          ) : null}
          {dismissButton}
        </div>
      </div>
    )
  }

  if (lastResult.notice === 'returned_empty') {
    return (
      <div className="pantry-add-feedback pantry-add-feedback--notice" role="status">
        <span className="pantry-add-feedback__icon" aria-hidden="true">
          ℹ
        </span>
        <div className="pantry-add-feedback__body">
          <div className="pantry-add-feedback__title">Brought you back here</div>
          <div className="pantry-add-feedback__sub">
            You removed every item from the review, so nothing was added. Your pasted text is still
            here — edit it and try again.
          </div>
        </div>
        {dismissButton}
      </div>
    )
  }

  if (lastResult.error) {
    return (
      <div className="pantry-add-feedback pantry-add-feedback--error" role="alert">
        <span className="pantry-add-feedback__icon" aria-hidden="true">
          ⚠
        </span>
        <div className="pantry-add-feedback__body">
          <div className="pantry-add-feedback__title">
            {lastResult.errorTitle || 'Couldn’t update your pantry.'}
          </div>
          <div className="pantry-add-feedback__sub">
            {lastResult.error || 'Your pantry wasn’t changed. Try again.'}
          </div>
        </div>
        <div className="pantry-add-feedback__actions">
          {onTryManual ? (
            <button type="button" className="pantry-add-feedback__retry" onClick={onTryManual}>
              Try again
            </button>
          ) : null}
          {dismissButton}
        </div>
      </div>
    )
  }

  const added = lastResult.added ?? 0
  const updated = lastResult.updated ?? 0
  const removed = lastResult.removed ?? 0
  const failed = lastResult.failed ?? 0
  const hasSuccess = added > 0 || updated > 0 || removed > 0
  const skippedItems = unrecognizedItems || []
  const hasPartial = hasReceiptSkippedItems(skippedItems)

  if (!hasSuccess && !hasPartial) return null

  const partialTitle = hasSuccess
    ? `${skippedItems.length} item${skippedItems.length !== 1 ? 's' : ''} not added`
    : 'No items were added'

  return (
    <div className="pantry-add-feedback-stack">
      {hasSuccess ? (
        <div
          className={`pantry-add-feedback pantry-add-feedback--success${failed > 0 ? ' pantry-add-feedback--partial-fail' : ''}`}
          role="status"
        >
          <span className="pantry-add-feedback__icon" aria-hidden="true">
            {failed > 0 ? '!' : '✓'}
          </span>
          <div className="pantry-add-feedback__body">
            {removed > 0 ? (
              <>
                <div className="pantry-add-feedback__title">
                  {removed} item{removed !== 1 ? 's' : ''} removed from your pantry
                </div>
              </>
            ) : (
              <>
                <div className="pantry-add-feedback__title">
                  {added > 0
                    ? `${added} item${added !== 1 ? 's' : ''} added to your pantry`
                    : `${updated} item${updated !== 1 ? 's' : ''} updated`}
                  {added > 0 && updated > 0 ? ` · ${updated} updated` : ''}
                </div>
                <div className="pantry-add-feedback__sub">
                  {failed > 0
                    ? `${failed} item${failed !== 1 ? 's' : ''} couldn't be added — you can re-add ${failed !== 1 ? 'them' : 'it'} from "Add items."`
                    : addedItemNames.length > 0
                      ? `${addedItemNames.slice(0, 4).join(', ')}${addedItemNames.length > 4 ? `, and ${addedItemNames.length - 4} more` : ''} — highlighted below.`
                      : "They're highlighted in green below — check that names look right."}
                </div>
              </>
            )}
          </div>
        </div>
      ) : null}

      {hasPartial ? (
        <div className="pantry-add-feedback pantry-add-feedback--partial" role="status">
          <div className="pantry-add-feedback__partial-header">
            <div className="pantry-add-feedback__body">
              <div className="pantry-add-feedback__title">{partialTitle}</div>
              <div className="pantry-add-feedback__sub">
                {hasSuccess
                  ? 'These lines were skipped — add them manually if you still need them.'
                  : 'We could not add the following — try adding them one at a time under Add items.'}
              </div>
            </div>
            {dismissButton}
          </div>
          <ul className="pantry-add-feedback__unrecognized-list">
            {skippedItems.map((n, i) => (
              <li key={i} className="pantry-add-feedback__unrecognized-row">
                <span>{n.item_name}</span>
                <span className="pantry-add-feedback__reason-tag">
                  {receiptSkipReasonLabel(n.reason, n.item_name)}
                </span>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </div>
  )
}
